import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { AppDrawer, AppLayout, AppLayoutContent } from '@/shared/ui'
import { useTranslation } from 'react-i18next'

// Views
import HeaderScene from '../Components/Header/Scenes'
import { SideMenu } from '../../../../../../refactory/modules/layout/infrastructure/side-menu/side-menu.component'
import { ModulePerms } from '../../../../../../refactory/modules/access-control/domain/module'
import { Can } from '../../../../../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import LazyLoad from '../../../../LazyLoad'

import './styles.less'
import { useGateValue } from '@statsig/react-bindings'
import { drawerMenuBodyStyle } from '../../../../../../refactory/modules/layout/infrastructure/side-menu/SideMenu.styles'
import { isWebview } from '../../../../../../utils/isWebview'
import { applyWebviewHeight } from '../../../../../../utils/webviewHeight'

const LazyLoadJobList = React.lazy(() => import('@/domain/jobs'))
const LazyLoadJobs = React.lazy(() => import('@/domain/flag-management'))
const LazyLoadResources = React.lazy(() => import('@/domain/resource-management'))

const LazyLoadRoutesV2 = React.lazy(() =>
  import('../../../../../../refactory/modules/routing/infrastructure/Routing').then((m) => ({ default: m.Routing }))
)
const LazyLoadSystemParameters = React.lazy(() => import('@/domain/system-parameters'))
const LazyLoadUsers = React.lazy(() => import('@/domain/user-management'))
const LazyLoadMetrics = React.lazy(() => import('@/domain/metrics'))
const LazyLoadAnalytics = React.lazy(() =>
  import('../../../../../../refactory/analytics/infrastructure/analytics-visualization/analytics.component')
)
const LazyLoadMFE = React.lazy(() => import('@/domain/bp'))
const LazyLoadMFEVSN = React.lazy(() => import('@/domain/vision'))
const LazyLoadMFEInboxWeb = React.lazy(() => import('@/domain/inbox-web'))
const LazyLoadMFEWMS = React.lazy(() => import('@/domain/alfred'))
const LazyLoadMFESegmenter = React.lazy(() => import('@/domain/segmenter'))
const LazyLoadMFEComanda = React.lazy(() => import('@/domain/venom'))
const LazyLoadMFESage = React.lazy(() => import('@/domain/sage'))
const LazyLoadMFEJonahJameson = React.lazy(() => import('@/domain/jonah-jameson'))
const LazyLoadMFELapdocs = React.lazy(() => import('@/domain/leapdocs'))
const LazyLoadMFECallCenter = React.lazy(() => import('@/domain/call-center'))
const LazyLoadActivityLog = React.lazy(() =>
  import('../../../../../../refactory/activity-log/infrastructure/get-events/events-visualization.component')
)
const LazyLoadStartStream = React.lazy(() =>
  import('../../../../../../refactory/modules/start-stream/infrastructure/reports.component')
)
const LazyLoadNotAvailable = React.lazy(() =>
  import('../../../../../../refactory/modules/not-available/infrastructure/not-available.component')
)
const LazyLoadRejectItemsCreatePostDelivery = React.lazy(() =>
  import(
    '../../../../../../refactory/modules/jobs/infrastructure/reject-items-post-delivery-incidence/reject-items-post-delivery-creation-view.component'
  )
)
const LazyLoadRejectItemsResolutionPostDelivery = React.lazy(() =>
  import(
    '../../../../../../refactory/modules/jobs/infrastructure/reject-items-post-delivery-incidence/reject-items-post-delivery-resolution-view.component'
  )
)
const LazyLoadTasks = React.lazy(() =>
  import('../../../../../../refactory/tasks/infrastructure/tasks/tasks-view.component')
)
const LazyLoadMarketplaceManager = React.lazy(() =>
  import(
    '../../../../../../refactory/modules/marketplace-manager/infrastructure/features/marketplace-manager/MarketplaceManager'
  ).then((m) => ({ default: m.MarketplaceManager }))
)
const HAS_ROUTES = 'hasRoutes'
const HAS_ACCOUNTING = 'hasAccounting'

export default withRouter((props) => {
  const { currentUser, history, featureFlags } = props
  const { t } = useTranslation()
  const [state, setState] = useState({
    // Lazy from the first paint — the effect below replaces it with the view
    // for props.view. Importing FlagManagement statically pulled ~1MB (job
    // detail, chat drawer, react-pdf...) into the initial bundle.
    viewToShow: <LazyLoad component={LazyLoadJobs} {...props} />
  })
  const [title, setTitle] = useState('')
  const isComandaMenuActive = useGateValue('comanda_menu')
  const isSageMenuActive = useGateValue('is_sage_menu_active')
  const isEcommerceMenuActive = useGateValue('is_ecommerce_menu_active')
  const isWmsMenuActive = useGateValue('is_wms_menu_active')
  const isMetabaseActive = useGateValue('is_metabase_active')
  const isStarStreamActive = useGateValue('is_star_stream_active')
  const isNewRejectItemsPostDeliveryActive = useGateValue('is_new_reject_items_active')

  const getAllowedView = (viewKey) => {
    const views = [
      {
        key: 'orders',
        path: '/orders',
        isAvailable: Can([ModulePerms.JOB])
      },
      {
        key: 'tasks',
        path: '/tasks',
        isAvailable: Can([ModulePerms.TAS])
      },
      {
        key: 'order-incidence-create',
        path: '/orders',
        isAvailable: Can([ModulePerms.JOB]) && isNewRejectItemsPostDeliveryActive
      },
      {
        key: 'order-incidence-resolve',
        path: '/orders',
        isAvailable: Can([ModulePerms.JOB]) && isNewRejectItemsPostDeliveryActive
      },
      {
        key: 'alert-management',
        path: '/alert-management/orders',
        isAvailable: Can([ModulePerms.FLA])
      },
      {
        key: 'routes',
        path: '/routes',
        isAvailable: Can([ModulePerms.ROU]) && featureFlags[HAS_ROUTES] === 'true'
      },
      {
        key: 'metrics',
        path: '/metrics/order-status',
        isAvailable: Can([ModulePerms.MET])
      },
      {
        key: 'analytics',
        path: '/analytics',
        isAvailable: Can([ModulePerms.ANA]) && isMetabaseActive
      },
      {
        key: 'users',
        path: '/user-management/users',
        isAvailable: Can([ModulePerms.USE])
      },
      {
        key: 'resources',
        path: '/resources/stores',
        isAvailable: Can([ModulePerms.RES])
      },
      {
        key: 'system-parameters',
        path: '/system-parameters/general',
        isAvailable: Can([ModulePerms.PAR])
      },
      {
        key: 'accounting',
        path: '/accounting',
        isAvailable: Can([ModulePerms.ACC]) && featureFlags[HAS_ACCOUNTING] === 'true'
      },
      {
        key: 'ecommerceAdmin',
        path: '/ecommerceAdmin',
        isAvailable: Can([ModulePerms.ECO]) && isEcommerceMenuActive
      },
      {
        key: 'inbox',
        path: '/inbox',
        isAvailable: Can([ModulePerms.INB])
      },
      {
        key: 'wms',
        path: '/wms',
        isAvailable: Can([ModulePerms.WMS]) && isWmsMenuActive
      },
      {
        key: 'segmenter',
        path: '/segmenter',
        isAvailable: Can([ModulePerms.SEG])
      },
      {
        key: 'comanda',
        path: '',
        isAvailable: Can([ModulePerms.COM]) && isComandaMenuActive
      },
      {
        key: 'order-ratings',
        path: '/order-ratings',
        isAvailable: Can([ModulePerms.ORA]) && isSageMenuActive
      },
      {
        key: 'jonah-jameson',
        path: '/jonah-jameson',
        isAvailable: Can([ModulePerms.JJS])
      },
      {
        key: 'activity-log',
        path: '/activity-log',
        isAvailable: Can([ModulePerms.LOG])
      },
      {
        key: 'reports',
        path: '/reports',
        isAvailable: Can([ModulePerms.SST]) && isStarStreamActive
      },
      {
        key: 'not-found',
        path: '/not-found',
        isAvailable: true
      },
      {
        key: 'leapdocs',
        path: '/leapdocs',
        isAvailable: true
      },
      {
        key: 'marketplace-manager',
        path: '/marketplace-manager',
        isAvailable: Can([ModulePerms.MAR])
      },
      {
        key: 'call-center',
        path: '/call-center',
        isAvailable: Can([ModulePerms.CAL])
      }
    ]
    const viewAvailable = views.find((view) => view.key === viewKey)
    const fisrtViewAvailable = views.find((view) => view.isAvailable && view.key !== 'comanda')

    if (viewAvailable.isAvailable) {
      return viewAvailable.key
    } else {
      history.push(fisrtViewAvailable.path)
      return fisrtViewAvailable.key
    }
  }

  useEffect(() => {
    let allowedView = ''
    if (Object.keys(featureFlags).length > 0) {
      allowedView = getAllowedView(props.view)
    } else {
      allowedView = props.view
    }
    const getView = (view) => {
      switch (view) {
        case 'marketplace-manager':
          setTitle(t('marketPlaceManager'))
          return <LazyLoad component={LazyLoadMarketplaceManager} {...props} />
        case 'orders':
          setTitle(t('menu.jobs-search'))
          return <LazyLoad component={LazyLoadJobList} {...props} />
        case 'tasks':
          setTitle(t('menu.tasks'))
          return <LazyLoad component={LazyLoadTasks} {...props} />
        case 'order-incidence-create':
          setTitle(t('jobs.rejectItemsView.title'))
          return <LazyLoad component={LazyLoadRejectItemsCreatePostDelivery} {...props} />
        case 'order-incidence-resolve':
          setTitle(t('jobs.rejectItemsView.title'))
          return <LazyLoad component={LazyLoadRejectItemsResolutionPostDelivery} {...props} />
        case 'alert-management':
          setTitle(t('menu.jobs-list'))
          return <LazyLoad component={LazyLoadJobs} {...props} />
        case 'routes':
          setTitle(t('routing-module'))
          return <LazyLoad component={LazyLoadRoutesV2} {...props} />
        case 'metrics':
          setTitle(t('metrics'))
          return <LazyLoad component={LazyLoadMetrics} {...props} />
        case 'analytics':
          setTitle(t('metabase.analytics'))
          return <LazyLoad component={LazyLoadAnalytics} {...props} />
        case 'users':
          setTitle(t('menu.users'))
          return <LazyLoad component={LazyLoadUsers} {...props} />
        case 'resources':
          setTitle(t('menu.resources'))
          return <LazyLoad component={LazyLoadResources} {...props} />
        case 'system-parameters':
          setTitle(t('systemParameters'))
          return <LazyLoad component={LazyLoadSystemParameters} {...props} />
        case 'accounting':
          setTitle(t('Accounting'))
          return <LazyLoad component={LazyLoadMFE} {...props} />
        case 'ecommerceAdmin':
          setTitle(t('E-commerce Admin'))
          return <LazyLoad component={LazyLoadMFEVSN} {...props} />
        case 'inbox':
          setTitle(t('menu.inbox'))
          return <LazyLoad component={LazyLoadMFEInboxWeb} {...props} />
        case 'wms':
          setTitle('WMS')
          return <LazyLoad component={LazyLoadMFEWMS} {...props} />
        case 'segmenter':
          setTitle(t('Segmenter'))
          return <LazyLoad component={LazyLoadMFESegmenter} {...props} />
        case 'comanda':
          setTitle(t('Comanda'))
          return <LazyLoad component={LazyLoadMFEComanda} {...props} />
        case 'order-ratings':
          setTitle(t('order-ratings'))
          return <LazyLoad component={LazyLoadMFESage} {...props} />
        case 'jonah-jameson':
          setTitle('Jonah jameson')
          return <LazyLoad component={LazyLoadMFEJonahJameson} {...props} />
        case 'activity-log':
          setTitle(t('activityLog.activityLog'))
          return <LazyLoad component={LazyLoadActivityLog} {...props} />
        case 'reports':
          setTitle(t('reports.title'))
          return <LazyLoad component={LazyLoadStartStream} {...props} />
        case 'not-found':
          setTitle('')
          return <LazyLoad component={LazyLoadNotAvailable} {...props} />
        case 'leapdocs':
          setTitle(t('leapdocs'))
          return <LazyLoad component={LazyLoadMFELapdocs} {...props} />
        case 'call-center':
          setTitle(t('roles-permissions.roles.call_center'))
          return <LazyLoad component={LazyLoadMFECallCenter} {...props} />
        default:
          return <LazyLoad component={LazyLoadNotAvailable} {...props} />
      }
    }

    setState({ viewToShow: getView(allowedView) })
  }, [props.view, currentUser, props.featureFlags, props, t])

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  useEffect(() => {
    let resizeTimeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768)
      }, 100)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [])

  useEffect(() => {
    if (!isWebview()) return
    const cleanupHeight = applyWebviewHeight()
    document.documentElement.classList.add('is-webview')
    return () => {
      document.documentElement.classList.remove('is-webview')
      cleanupHeight()
    }
  }, [])

  const [sideMenuVisible, setSideMenuVisible] = useState(false)
  return (
    <AppLayout className='main-screen' hasSider={!isMobile}>
      {!isMobile ? (
        <SideMenu history={props.history} view={props.view} featureFlags={props.featureFlags} />
      ) : (
        <AppDrawer
          placement='left'
          closable={true}
          onClose={() => setSideMenuVisible(false)}
          open={sideMenuVisible}
          width={200}
          styles={{ body: drawerMenuBodyStyle }}
        >
          <SideMenu
            history={props.history}
            view={props.view}
            featureFlags={props.featureFlags}
            isMobile
            onClick={() => setSideMenuVisible(false)}
          />
        </AppDrawer>
      )}
      <AppLayout>
        <HeaderScene
          {...props}
          onMenuToggle={() => setSideMenuVisible(!sideMenuVisible)}
          isMobile={isMobile}
          title={title}
        />
        <AppLayoutContent>{state.viewToShow}</AppLayoutContent>
      </AppLayout>
    </AppLayout>
  )
})
